import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ElementRef,
    Renderer2
} from '@angular/core';

@Component({
    selector: 'app-modal',
    standalone: false,
    templateUrl: './modal.component.html',
    styleUrl: './modal.component.css'
})
export class ModalComponent implements OnChanges, OnDestroy {
    @Input() modalTitle: string = '';
    @Input() isOpen: boolean = false;
    @Input() minHeight: string = 'auto';
    @Input() maxWidth: string = 'max-w-lg';
    /** When true, the header close (X) icon is hidden. */
    @Input() hideCloseButton: boolean = false;
    /** When true, clicking the backdrop closes the modal. Default false so popovers only close via X. */
    @Input() closeOnBackdropClick: boolean = false;
    /** Stacking order for nested modals (e.g. scanner setup above patient details). */
    @Input() zIndex = 100000;
    /** When true, portal the modal host to document.body so it stacks above parent popovers. */
    @Input() appendToBody = false;
    @Output() close = new EventEmitter<void>();

    private originalParent: Node | null = null;
    private originalNextSibling: Node | null = null;
    private isPortaledToBody = false;

    constructor(
        private cdr: ChangeDetectorRef,
        private el: ElementRef<HTMLElement>,
        private renderer: Renderer2
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isOpen'] || changes['appendToBody']) {
            this.syncBodyPortal();
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy(): void {
        this.restoreFromBody();
    }

    private syncBodyPortal(): void {
        if (this.appendToBody && this.isOpen) {
            this.moveToBody();
            return;
        }
        this.restoreFromBody();
    }

    private moveToBody(): void {
        const host = this.el.nativeElement;
        if (host.parentNode === document.body) {
            this.isPortaledToBody = true;
            return;
        }
        this.originalParent = host.parentNode;
        this.originalNextSibling = host.nextSibling;
        this.renderer.appendChild(document.body, host);
        this.isPortaledToBody = true;
    }

    private restoreFromBody(): void {
        const host = this.el.nativeElement;
        if (!this.isPortaledToBody || !this.originalParent || host.parentNode !== document.body) {
            return;
        }
        if (this.originalNextSibling && this.originalNextSibling.parentNode === this.originalParent) {
            this.renderer.insertBefore(this.originalParent, host, this.originalNextSibling);
        } else {
            this.renderer.appendChild(this.originalParent, host);
        }
        this.isPortaledToBody = false;
        this.originalParent = null;
        this.originalNextSibling = null;
    }

    onClose(): void {
        this.close.emit();
    }

    onBackdropClick(event: MouseEvent): void {
        if (this.closeOnBackdropClick && (event.target as HTMLElement).classList.contains('modal-backdrop')) {
            this.onClose();
        }
    }
}
